import { z } from 'zod';

import { EntityBaseSchema, IdSchema } from '../../base';
import {
  ErrorsUpdateSchema,
  FunctionCreateFunctionSchema,
  FunctionErrorSchema,
  FunctionIdSchema,
  LatencyControlCommandSchema,
} from '../functions';
import {
  AsrKeywordsSchema,
  BaseConditionSchema,
  BaseFlowSchema,
  BaseFlowStepSchema,
  BaseNoCodeStepSchema,
  ConditionIdSchema,
  FlowIdSchema,
  NoCodeStepReferencesSchema,
  StepAsrBiasingSchema,
  StepDTMFConfigSchema,
  StepIdSchema,
  StepNameSchema,
  StepPositionSchema,
  StepReferencesSchema,
  TransitionFunctionReferencesSchema,
} from './flow.schema';

export const UpdateStepReferencesSchema = StepReferencesSchema.partial();

export const TransitionFunctionIdSchema = FlowIdSchema.merge(FunctionIdSchema);

export const UpdateAsrKeywordsSchema = z.object({
  customKeywords: AsrKeywordsSchema,
});

export const UpdateFlowStepAsrConfigSchema = StepIdSchema.extend({
  asrBiasing: StepAsrBiasingSchema.extend({
    customKeywords: UpdateAsrKeywordsSchema,
  }).partial(),
});

export const UpdateFlowStepDTMFConfigSchema = StepIdSchema.extend({
  dtmfConfig: StepDTMFConfigSchema.partial(),
});

const StepPositionUpdateSchema = z.object({
  stepId: IdSchema,
  newPosition: StepPositionSchema,
});

export const UpdateFlowStepPositionSchema = FlowIdSchema.extend({
  positionUpdate: StepPositionUpdateSchema,
});

export const UpdateFlowStepPositionsSchema = FlowIdSchema.extend({
  newPositions: z.array(StepPositionUpdateSchema),
});

export const CreateStepSchema = EntityBaseSchema.merge(
  BaseFlowStepSchema,
).extend({
  position: StepPositionSchema,
  asrBiasing: StepAsrBiasingSchema.optional(),
  dtmfConfig: StepDTMFConfigSchema.optional(),
});

export const FlowCreateStepSchema = FlowIdSchema.extend({
  step: CreateStepSchema,
});

export const UpdateFlowStepSchema = FlowIdSchema.extend({
  step: EntityBaseSchema.merge(BaseFlowStepSchema.partial()),
});

export const DeleteFlowStepSchema = StepIdSchema;

export const TransitionFunctionCreateFunctionSchema =
  FunctionCreateFunctionSchema.extend({
    references: TransitionFunctionReferencesSchema,
    latencyControl: LatencyControlCommandSchema.optional(),
  });

export const UpdateFlowSchema = FlowIdSchema.merge(BaseFlowSchema.partial())
  .extend({
    oldFlowName: z
      .string()
      .optional()
      .describe(
        'The old name of the flow. This enables us to update conv.goto_flow references in global functions when the flow name changes.',
      ),
  })
  .describe("Update a flow's properties (name, description, or start step)");

export const DeleteFlowSchema = FlowIdSchema.describe(
  'Delete a conversation flow by its ID',
);

export type StepReferences = z.infer<typeof StepReferencesSchema>;
export type UpdateStepReferences = z.infer<typeof UpdateStepReferencesSchema>;

export const TransitionFunctionSchema =
  TransitionFunctionCreateFunctionSchema.extend({
    latencyControl: LatencyControlCommandSchema,
    errors: z.array(FunctionErrorSchema),
  });

export const UpdateNoCodeStepReferencesSchema =
  NoCodeStepReferencesSchema.partial();

// no code step
export const CreateNoCodeStepSchema = StepIdSchema.merge(BaseNoCodeStepSchema)
  .omit({
    conditions: true,
  })
  .describe(
    'Add a new step to an existing flow. ' +
      'IMPORTANT: New steps are created without connections. After creating, you MUST create conditions to connect it to the flow. ' +
      'If inserting between existing steps: 1) Delete the old connection first, 2) Create connection TO the new step, 3) Create connection FROM the new step.',
  );

export const UpdateNoCodeStepSchema = StepIdSchema.merge(
  BaseNoCodeStepSchema.partial(),
)
  .omit({
    conditions: true,
  })
  .describe(
    "Update an existing step's properties (name, prompt, position, or references)",
  );

export const DeleteNoCodeStepSchema = StepIdSchema.describe(
  'Delete a step from a flow. ' +
    'IMPORTANT: Before deleting, check what connections point TO and FROM this step. ' +
    'You may need to reconnect surrounding steps to maintain flow continuity.',
);

export const UpdateNoCodeStepPositionSchema = StepIdSchema.merge(
  z.object({ newPosition: StepPositionSchema }),
);

// no code condition
export const CreateNoCodeConditionSchema = ConditionIdSchema.merge(
  BaseConditionSchema,
).describe(
  'Create a connection (condition) between steps in a flow. ' +
    'IMPORTANT: If replacing an existing connection (e.g., inserting a step in the middle), delete the old condition FIRST to avoid duplicate paths.',
);

export const UpdateNoCodeConditionSchema = ConditionIdSchema.merge(
  BaseConditionSchema.partial(),
).describe("Update an existing condition's configuration");

export const DeleteNoCodeConditionSchema = ConditionIdSchema.describe(
  'Delete a condition (connection) from a step. ' +
    'Use this when: 1) Removing a path entirely, or 2) Before inserting a new step between two connected steps.',
);

export const UpdateNoCodeConditionPositionSchema = ConditionIdSchema.merge(
  z.object({ newPosition: StepPositionSchema }),
);

export type NoCodeStepReferences = z.infer<typeof NoCodeStepReferencesSchema>;
export type UpdateNoCodeStepReferences = z.infer<
  typeof UpdateNoCodeStepReferencesSchema
>;

// exit flow for no code step
export const UpdateNoCodeConditionExitFlowPositionSchema =
  ConditionIdSchema.merge(z.object({ newPosition: StepPositionSchema }));

export const CreateFlowSchema = EntityBaseSchema.merge(BaseFlowSchema)
  .extend({
    noCodeSteps: z
      .array(CreateNoCodeStepSchema)
      .default([])
      .describe(
        "Array of conversation steps. Each step represents a point in the conversation. Step types: 'say' (bot speaks to user), 'ask' (bot asks question and waits for response), 'functionCall' (call external API or function), 'end' (exit conversation). Steps must be explicitly connected using conditions to create the conversation flow. Every step (except 'end') needs at least one outgoing condition.",
      ),
    steps: z
      .array(CreateStepSchema)
      .describe('Legacy step format - use noCodeSteps for new flows'),
    transitionFunctions: z
      .array(TransitionFunctionCreateFunctionSchema)
      .describe(
        'Functions that run between steps to process data, make decisions, or call external APIs',
      ),
  })
  .describe(
    'Create a new conversation flow with steps and connections. ' +
      'IMPORTANT: After creating a flow, you must also create a Knowledge Base topic as an entry point - ' +
      'this allows users to trigger the flow with natural language queries.',
  );

export const CreateFunctionStepPayloadSchema = EntityBaseSchema.extend({
  name: StepNameSchema,
  position: StepPositionSchema,
  function: FunctionCreateFunctionSchema.omit({
    references: true,
    description: true,
    parameters: true,
  }),
});

// Create Step - Matches proto structure with oneof payload
export const GenericCreateStepSchema = FlowIdSchema.extend({
  payload: z.discriminatedUnion('$case', [
    z.object({
      $case: z.literal('functionStep'),
      value: CreateFunctionStepPayloadSchema,
    }),
    // Future: Add other step types here
    // z.object({ $case: z.literal("default_step"), value: ... }),
  ]),
});

export const FunctionStepCreateFunctionSchema =
  FunctionCreateFunctionSchema.omit({
    references: true,
    description: true,
    parameters: true,
  }).extend({
    code: z.string(),
  });

export const FunctionStepUpdateFunctionDefinitionSchema = z.object({
  description: z.string().optional(),
  code: z.string().optional(),
  errors: ErrorsUpdateSchema.optional(),
  latencyControl: LatencyControlCommandSchema.optional(),
});

// Function Step Update Payload Schema (matches proto UpdateFunctionStep)
const UpdateFunctionStepPayloadSchema = z.object({
  name: StepNameSchema.optional(),
  position: StepPositionSchema.optional(),
  function: FunctionStepUpdateFunctionDefinitionSchema.optional(),
});

// Update Step - Matches proto structure with oneof payload
export const GenericUpdateStepSchema = StepIdSchema.extend({
  payload: z.discriminatedUnion('$case', [
    z.object({
      $case: z.literal('functionStep'),
      value: UpdateFunctionStepPayloadSchema,
    }),
    // Future: Add other step types here
    // z.object({ $case: z.literal("default_step"), value: ... }),
  ]),
});

// Delete Step - Simple schema matching proto (no payload discrimination needed)
export const GenericDeleteStepSchema = StepIdSchema;

// Step Condition Schemas
export const CreateStepConditionSchema =
  ConditionIdSchema.merge(BaseConditionSchema);
export const UpdateStepConditionSchema = ConditionIdSchema.merge(
  BaseConditionSchema.partial(),
);

export const DeleteStepConditionSchema = ConditionIdSchema;
export const UpdateStepConditionPositionSchema = ConditionIdSchema.merge(
  z.object({ newPosition: StepPositionSchema }),
);

export const MoveFlowStepPositionDetailSchema = z.object({
  $case: z.literal('flowStep'),
  value: z.object({
    stepId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFlowStepPositionDetail = z.infer<
  typeof MoveFlowStepPositionDetailSchema
>;

export const MoveFlowNoCodeStepPositionDetailSchema = z.object({
  $case: z.literal('noCodeStep'),
  value: z.object({
    stepId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFlowNoCodeStepPositionDetail = z.infer<
  typeof MoveFlowNoCodeStepPositionDetailSchema
>;

export const MoveFlowNoCodeStepConditionDetailSchema = z.object({
  $case: z.literal('noCodeStepCondition'),
  value: z.object({
    stepId: IdSchema,
    conditionId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFlowNoCodeStepConditionDetail = z.infer<
  typeof MoveFlowNoCodeStepConditionDetailSchema
>;

export const MoveFlowNoCodeStepConditionExitFlowDetailSchema = z.object({
  $case: z.literal('noCodeStepConditionExitFlow'),
  value: z.object({
    stepId: IdSchema,
    conditionId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFlowNoCodeStepConditionExitFlowDetail = z.infer<
  typeof MoveFlowNoCodeStepConditionExitFlowDetailSchema
>;

export const MoveFunctionStepPositionDetailSchema = z.object({
  $case: z.literal('functionStep'),
  value: z.object({
    stepId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFunctionStepPositionDetail = z.infer<
  typeof MoveFunctionStepPositionDetailSchema
>;

export const MoveFunctionStepConditionDetailSchema = z.object({
  $case: z.literal('functionStepCondition'),
  value: z.object({
    stepId: IdSchema,
    conditionId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFunctionStepConditionDetail = z.infer<
  typeof MoveFunctionStepConditionDetailSchema
>;

export const MoveFunctionStepConditionExitFlowDetailSchema = z.object({
  $case: z.literal('functionStepConditionExitFlow'),
  value: z.object({
    stepId: IdSchema,
    conditionId: IdSchema,
    newPosition: StepPositionSchema,
  }),
});

export type MoveFunctionStepConditionExitFlowDetail = z.infer<
  typeof MoveFunctionStepConditionExitFlowDetailSchema
>;

export const FlowStepPositionDetailSchemaDiscriminated = z.discriminatedUnion(
  '$case',
  [
    MoveFlowStepPositionDetailSchema,
    MoveFlowNoCodeStepPositionDetailSchema,
    MoveFlowNoCodeStepConditionDetailSchema,
    MoveFlowNoCodeStepConditionExitFlowDetailSchema,
    MoveFunctionStepPositionDetailSchema,
    MoveFunctionStepConditionDetailSchema,
    MoveFunctionStepConditionExitFlowDetailSchema,
  ],
);

export const FlowStepPositionDetailSchema = z.object({
  detail: FlowStepPositionDetailSchemaDiscriminated,
});

export const MoveFlowComponentSchema = FlowIdSchema.merge(
  z.object({
    positionDetail: FlowStepPositionDetailSchema,
  }),
);

export const MoveFlowComponentsSchema = FlowIdSchema.merge(
  z.object({
    positionDetails: z.array(FlowStepPositionDetailSchema),
  }),
);
